import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TickerEntry, TickerMode } from '../../models/new-release-page.types';
// TickerMode is now a single-value type ('recently_sold'); kept as an Input
// so the page-level contract can extend later without breaking consumers.

// The trial design's hero ticker visual diverges enough from /sell's
// pla-plate-x-pulse-rail (front/rear plate pills, white-on-dark, sold-style
// price formatting) that this is a sibling component rather than a reuse.
// Behavioural sibling — same marquee idea, different chrome.
//
// Architecture recommendation (future): keep these as siblings until a
// third use case shows up. Two callers don't justify a parametric base —
// the data shapes (PulseItem {when,text} vs TickerEntry {plate,price})
// and chrome differ enough that an abstraction would be either narrow
// (only covers what the existing two share) or leaky (the base component
// has to know about both data shapes and toggle chrome on a mode flag).
// Rule of three: revisit if a third ticker variant appears, and at that
// point extract the marquee mechanic (doubled list + linear translate
// loop) into a presentational base, leaving the chrome + data mapping in
// the variants.

@Component({
  selector: 'pla-new-release-ticker',
  templateUrl: './new-release-ticker.component.html',
  styleUrls: ['./new-release-ticker.component.scss'],
})
export class NewReleaseTickerComponent {
  @Input() label: string = 'Recently sold';
  @Input() mode: TickerMode = 'recently_sold';
  @Input() entries: TickerEntry[] = [];

  constructor(private router: Router) {}

  onTickClick(entry: TickerEntry): void {
    const reg = (entry.plate || '').replace(/\s+/g, '');
    if (!reg) return;
    this.router.navigate(['/plate', reg]);
  }

  // Duplicate the list so the marquee loop is seamless (transform -50%).
  // Memoised on the input reference: a fresh array on every change-detection
  // pass makes *ngFor's binding look changed on each check.
  private doubledFor: TickerEntry[] | null = null;
  private doubled: TickerEntry[] = [];
  get doubledEntries(): TickerEntry[] {
    if (this.doubledFor !== this.entries) {
      this.doubledFor = this.entries;
      this.doubled = [...(this.entries || []), ...(this.entries || [])];
    }
    return this.doubled;
  }

  // Alternate front-white / rear-yellow for visual interest, matching trial.
  variantFor(index: number): 'front' | 'rear' {
    return index % 2 === 0 ? 'front' : 'rear';
  }

  formatPrice(price: number): string {
    return `\u00A3${price.toLocaleString('en-GB')}`;
  }

  // Ticker only ever shows real sold plates; the template hardcodes
  // data-verb="sold". `verbForMode` and the recently_listed / editors_picks
  // branches were removed alongside their data sources.
}
